import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Shield, ArrowRight } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../../lib/site";

const accidentGuides = [
  ["Car Accidents", "/accident-types/car-accidents"],
  ["Truck Accidents", "/accident-types/truck-accidents"],
  ["Motorcycle Accidents", "/accident-types/motorcycle-accidents"],
  ["Pedestrian Accidents", "/accident-types/pedestrian-accidents"],
  ["Bicycle Accidents", "/accident-types/bicycle-accidents"],
  ["Uber & Lyft Accidents", "/accident-types/rideshare-accidents"],
  ["Slip & Fall", "/accident-types/slip-and-fall"],
  ["Dog Bites", "/accident-types/dog-bites"],
  ["Wrongful Death", "/accident-types/wrongful-death"],
  ["Catastrophic Injuries", "/accident-types/catastrophic-injuries"],
];

const explore = [
  ["How It Works", "/"],
  ["About Us", "/about"],
  ["Resources", "/resources"],
  ["FAQs", "/faqs"],
  ["Blog", "/blog"],
  ["Author: Aaron Bankhurst", "/authors/aaron-bankhurst"],
  ["Editorial Standards", "/editorial-policy"],
  ["Corrections", "/corrections"],
  ["Free Case Review", "/contact"],
];

const cities = [
  ["Philadelphia", "philadelphia"],
  ["Pittsburgh", "pittsburgh"],
  ["Allentown", "allentown"],
  ["Erie", "erie"],
  ["Reading", "reading"],
  ["Scranton", "scranton"],
  ["Bethlehem", "bethlehem"],
  ["Lancaster", "lancaster"],
  ["Harrisburg", "harrisburg"],
  ["York", "york"],
  ["Wilkes-Barre", "wilkes-barre"],
  ["State College", "state-college"],
  ["Altoona", "altoona"],
  ["Easton", "easton"],
  ["Chester", "chester"],
  ["King of Prussia", "king-of-prussia"],
  ["Monroeville", "monroeville"],
  ["Greensburg", "greensburg"],
  ["Butler", "butler"],
  ["Mechanicsburg", "mechanicsburg"],
];

const linkCls = "text-sm text-bone/70 hover:text-terracotta transition";

export default function Footer() {
  return (
    <footer className="bg-forest text-bone mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-x-8 gap-y-12">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-terracotta grid place-items-center text-white font-display font-black text-lg">PA</div>
              <div className="font-display font-bold text-bone text-lg tracking-tight">Pennsylvania Accident Aid</div>
            </div>
            <p className="text-bone/70 text-sm max-w-xs leading-relaxed mb-6">
              Free, 24/7 help for accident victims across Pennsylvania. We connect you with experienced local
              attorneys who fight for what you deserve. No fees unless you win.
            </p>
            <div className="space-y-2 text-sm text-bone/80">
              <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 hover:text-terracotta transition" data-testid="footer-phone">
                <Phone className="w-4 h-4 shrink-0" /> {PHONE_DISPLAY}
              </a>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 shrink-0" /> Use the case review form</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 shrink-0" /> Serving all of Pennsylvania</div>
            </div>
          </div>

          {/* Accident Guides */}
          <div className="col-span-1">
            <div className="text-bone font-display font-bold text-base mb-4">Accident Guides</div>
            <ul className="space-y-2">
              {accidentGuides.map(([name, href]) => (
                <li key={href}>
                  <Link to={href} className={linkCls} data-testid={`footer-guide-${href.split("/").pop()}`}>{name}</Link>
                </li>
              ))}
              <li>
                <Link to="/accident-types" className="text-sm text-terracotta font-semibold hover:underline inline-flex items-center gap-1" data-testid="footer-all-accident-types">
                  All accident types <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Explore */}
          <div className="col-span-1">
            <div className="text-bone font-display font-bold text-base mb-4">Explore</div>
            <ul className="space-y-2">
              {explore.map(([name, href]) => (
                <li key={name}>
                  <Link to={href} className={linkCls}>{name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Pennsylvania Cities */}
          <div className="col-span-2">
            <div className="text-bone font-display font-bold text-base mb-4">Pennsylvania Cities</div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2">
              {cities.map(([name, slug]) => (
                <li key={slug}>
                  <Link to={`/pennsylvania-cities/${slug}`} className={linkCls} data-testid={`footer-city-${slug}`}>{name}</Link>
                </li>
              ))}
            </ul>
            <Link to="/pennsylvania-cities" className="text-sm text-terracotta font-semibold hover:underline inline-flex items-center gap-1 mt-4" data-testid="footer-all-cities">
              View all cities <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        <div className="border-t border-bone/10 mt-12 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-bone/60">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            © {new Date().getFullYear()} Pennsylvania Accident Aid. All rights reserved.
          </div>
          <div className="leading-relaxed max-w-2xl">
            Disclaimer: This site is not a law firm and does not provide legal advice. We connect
            visitors with independent attorneys for free, no-obligation consultations. Contacting us
            does not create an attorney-client relationship. Past results do not guarantee future
            outcomes.
          </div>
        </div>
      </div>
    </footer>
  );
}
